import React, { useState, useMemo } from 'react';
import ApplicantCard from '../ApplicantCard/ApplicantCard';
import SearchFilter from '../../../../SearchFilter';
import { useParams } from 'react-router-dom';
import useFetchApplicants from '../../../../../../hooks/applicants/useFetchApplicants';
import useFetchJobHirings from '../../../../../../hooks/hiring/useFetchJobHirings';
import { useRestaurant } from '../../../../../../context/RestaurantContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox, faUserTie, faRobot, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import './applicantView.css';

const ApplicantsView = ({ mode }) => {
    const { id } = useParams();
    const { employees, error, setEmployees } = useFetchApplicants(id, mode);
    const { restaurantId } = useRestaurant();
    const [searching, setSearching] = useState(false);
    const [aiScheduling, setAiScheduling] = useState(false);
    const [aiSchedulingError, setAiSchedulingError] = useState(null);
    const [activeTab, setActiveTab] = useState('postulantes'); // 'postulantes' or 'contratados'
    
    // Always fetch hired employees to filter them out from applicants
    const { hiredEmployees, loading: loadingHired, error: hiredError } = useFetchJobHirings(
        id,
        true // Always fetch to filter applicants
    );

    // Create a Set of all hired employee IDs (including completed)
    // Filter out applicants who have been hired at any point (active, accepted, or completed)
    const hiredEmployeeIds = useMemo(() => {
        return new Set(
            hiredEmployees
                .map(hired => hired.employeeId)
                .filter(Boolean)
        );
    }, [hiredEmployees]);

    // Filter out all hired employees from applicants list (including completed)
    // If someone was hired (even if completed), they shouldn't appear in applicants
    const filteredEmployees = useMemo(() => {
        if (activeTab === 'contratados') {
            return hiredEmployees;
        }
        // Filter out applicants who have been hired (any status: active, accepted, or completed)
        return employees.filter(employee => {
            const empId = employee.employeeId || employee.employee?.id;
            return !hiredEmployeeIds.has(empId);
        });
    }, [employees, hiredEmployees, hiredEmployeeIds, activeTab]);

    const handleSearchFilter = async (filters) => {
        if (activeTab === 'contratados') {
            // Search is not implemented for hired employees yet
            return;
        }
        
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const endpoint = `job-offers/${id}/applicants/search?${queryParams}&restaurantId=${restaurantId}`;

            const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/${endpoint}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch filtered data');
            const apiResponse = await response.json();
            
            // Extract applicants array from API response
            const applicantsArray = apiResponse.data || apiResponse.employees || apiResponse || [];
            // Filter out all hired employees from search results (including completed)
            const filteredApplicants = Array.isArray(applicantsArray) 
                ? applicantsArray.filter(employee => {
                    const empId = employee.employeeId || employee.employee?.id;
                    return !hiredEmployeeIds.has(empId);
                })
                : [];
            setEmployees(filteredApplicants);
        } catch (error) {
            console.error('Error fetching filtered data:', error);
        } finally {
            setSearching(false);
        }
    };

    const currentEmployees = filteredEmployees;
    const currentError = activeTab === 'contratados' ? hiredError : error;
    const isLoading = activeTab === 'contratados' ? loadingHired : false;

    return (
        <div className="applicant-view__container">
            <h1>Postulaciones</h1>
            <div className="applicant-view__button-toggle-container">
                <button
                    onClick={() => setActiveTab('postulantes')}
                    className={activeTab === 'postulantes' ? 'active' : ''}
                >
                    Postulantes
                </button>
                <button
                    onClick={() => setActiveTab('contratados')}
                    className={activeTab === 'contratados' ? 'active' : ''}
                >
                    Contratados
                </button> 
            </div>
            {activeTab === 'postulantes' && (
                <SearchFilter mode={mode} handleSearchFilter={handleSearchFilter} />
            )}
            <div className="applicant-view__applicants-container">
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#fb5424' }} />
                    </div>
                ) : Array.isArray(currentEmployees) && currentEmployees.length > 0 ? (
                    currentEmployees.map((employee) => (
                        <ApplicantCard
                            key={employee.id ?? employee.employeeId ?? employee.hiringId}
                            data={employee}
                            type="dashboard"
                            mode={activeTab === 'contratados' ? 'Hired' : 'Applicants'}
                            jobPostId={id}
                        />
                    ))
                ) : (
                    !currentError && (
                        <div className="applicant-view__empty-state">
                            <div className="empty-state__icon">
                                <FontAwesomeIcon icon={faInbox} />
                            </div>
                            <h2 className="empty-state__title">
                                {activeTab === 'contratados' 
                                    ? 'No hay empleados contratados aún' 
                                    : 'No hay postulaciones aún'}
                            </h2>
                            <p className="empty-state__description">
                                {activeTab === 'contratados'
                                    ? 'Los empleados contratados aparecerán aquí cuando acepten una oferta de trabajo.'
                                    : 'Las postulaciones aparecerán aquí cuando los candidatos se postulen a esta oferta de trabajo.'}
                            </p>
                            <div className="empty-state__info">
                                <div className="info-item">
                                    <FontAwesomeIcon icon={faUserTie} />
                                    <span>
                                        {activeTab === 'contratados'
                                            ? 'Envía ofertas de contratación desde las conversaciones'
                                            : 'Los candidatos pueden postularse desde la página de trabajos'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                )}
                {currentError && (
                    <div className="error-message">
                        <p>Error: {currentError.message}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicantsView;
